import { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, message, Card, Row, Col, Statistic } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, CheckCircleOutlined, StopOutlined, CopyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { surveyService, Survey, SurveyStats, Question, Option } from '../services/surveyService';
import SurveyEditor from '../components/SurveyEditor';
import SurveyPreview from '../components/SurveyPreview';

const QuestionnaireManagement: React.FC = () => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [stats, setStats] = useState<SurveyStats | null>(null);
  const [isEditorVisible, setIsEditorVisible] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState<Survey | null>(null);
  const [previewSurvey, setPreviewSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    page_size: 10,
    total: 0,
  });

  useEffect(() => {
    fetchSurveys();
    fetchStats();
  }, [pagination.current, pagination.page_size]);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const data = await surveyService.getSurveys({
        page: pagination.current,
        page_size: pagination.page_size,
      });
      setSurveys(data.data);
      setPagination(prev => ({
        ...prev,
        total: data.total
      }));
    } catch (error) {
      message.error('获取问卷列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await surveyService.getStats();
      setStats(data);
    } catch (error) {
      message.error('获取统计信息失败');
    }
  };

  const columns: ColumnsType<Survey> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 120,
      ellipsis: true,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      ellipsis: true,
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 60,
      render: (type: string) => {
        const typeMap = {
          'research': '调研问卷',
          'assistance': '援助问卷'
        };
        return typeMap[type as keyof typeof typeMap] || type;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 60,
      render: (status: string) => {
        const statusMap = {
          'draft': { text: '草稿', color: '#999' },
          'published': { text: '已发布', color: '#52c41a' },
          'ended': { text: '已结束', color: '#ff4d4f' },
        };
        const statusInfo = statusMap[status as keyof typeof statusMap] || { text: status, color: '#999' };
        return <span style={{ color: statusInfo.color }}>{statusInfo.text}</span>;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 400,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => handlePreview(record)}
          >
            预览
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record.id!)}
          >
            编辑
          </Button>
          {record.status === 'draft' && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => handlePublish(record.id!)}
            >
              发布
            </Button>
          )}
          {record.status === 'published' && (
            <Button
              type="primary"
              danger
              icon={<StopOutlined />}
              onClick={() => handleEnd(record.id!)}
            >
              结束
            </Button>
          )}
          {record.status === 'ended' && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => handlePublish(record.id!)}
            >
              重新发布
            </Button>
          )}
          <Button
            type="default"
            icon={<CopyOutlined />}
            onClick={() => handleCopy(record.id!)}
          >
            复制
          </Button>
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id!)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingSurvey(null);
    setIsEditorVisible(true);
  };

  const handleEdit = async (id: number) => {
    try {
      setLoading(true);
      const survey = await surveyService.getSurvey(id);
      if (!survey) {
        message.error('获取问卷数据失败');
        return;
      }
      
      // 确保问题数据格式正确
      const formattedSurvey = {
        ...survey,
        questions: survey.questions?.map(q => ({
          ...q,
          options: q.options || [],
          allow_other: q.allow_other || false,
          level: q.level || 0,
          parent_id: q.parent_id || null,
          section: q.section || '默认分组',
          answered_by: q.answered_by || 'user'
        }))
      };
      
      setEditingSurvey(formattedSurvey);
      setPreviewSurvey(formattedSurvey);
      setIsEditorVisible(true);
    } catch (error) {
      console.error('获取问卷数据失败', error);
      message.error('获取问卷数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (survey: Survey) => {
    // 确保问题数据格式正确
    const formattedSurvey = {
      ...survey,
      questions: survey.questions?.map(q => ({
        ...q,
        options: q.options || [],
        allow_other: q.allow_other || false,
        level: q.level || 0,
        parent_id: q.parent_id || null,
        section: q.section || '默认分组',
        answered_by: q.answered_by || 'user'
      }))
    };
    
    setPreviewSurvey(formattedSurvey);
    setIsPreviewVisible(true);
  };

  const handlePublish = async (id: number) => {
    try {
      await surveyService.publishSurvey(id);
      message.success('发布成功！');
      fetchSurveys();
      fetchStats();
    } catch (error) {
      message.error('发布失败');
    }
  };

  const handleEnd = async (id: number) => {
    try {
      await surveyService.endSurvey(id);
      message.success('问卷已结束！');
      fetchSurveys();
      fetchStats();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleCopy = async (id: number) => {
    try {
      await surveyService.copySurvey(id);
      message.success('问卷复制成功！');
      fetchSurveys();
      fetchStats();
    } catch (error) {
      message.error('复制失败');
    }
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个问卷吗？删除后无法恢复。',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await surveyService.deleteSurvey(id);
          message.success('问卷删除成功！');
          fetchSurveys();
          fetchStats();
        } catch (error) {
          message.error('删除失败');
        }
      }
    });
  };

  const handleSave = async (survey: Survey) => {
    try {
      // 处理问题数据格式
      const processedQuestions = survey.questions?.map((question, index) => ({
        ...question,
        id: question.id || Date.now() + index,
        order_num: question.order_num || index + 1,
        type: question.type as 'text' | 'radio' | 'checkbox' | 'textarea' | 'file' | 'sort',
        required: question.required || false,
        options: question.options?.map(option => ({
          ...option,
          has_input: option.has_input || false,
          input_label: option.input_label || '',
          has_multiple_inputs: option.has_multiple_inputs || false,
          input_labels: option.input_labels || [],
          has_sub_options: option.has_sub_options || false,
          sub_options: option.sub_options?.map(subOption => ({
            ...subOption,
            has_input: subOption.has_input || false,
            input_label: subOption.input_label || ''
          }))
        })) || [],
        allow_other: question.allow_other || false,
        level: question.level || 0,
        parent_id: question.parent_id || null,
        section: question.section || '默认分组',
        answered_by: question.answered_by as 'user' | 'approver' || 'user'
      })) || [];

      if (editingSurvey) {
        // 更新问卷（包括问题）
        await surveyService.updateSurvey(editingSurvey.id!, {
          title: survey.title,
          description: survey.description,
          type: survey.type,
          price: survey.price,
          questions: processedQuestions
        });
        message.success('更新成功！');
      } else {
        // 创建新问卷（包括问题）
        await surveyService.createSurvey({
          title: survey.title,
          description: survey.description,
          type: survey.type,
          price: survey.price,
          questions: processedQuestions
        });
        message.success('添加成功！');
      }
      
      setIsEditorVisible(false);
      fetchSurveys();
      fetchStats();
    } catch (error) {
      console.error('保存失败:', error);
      message.error('操作失败');
    }
  };



  const handleTableChange = (pagination: any) => {
    setPagination(pagination);
  };

  return (
    <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '24px' }}>
                  <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '16px' }}>问卷统计</h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '16px'
        }}>
          <Card>
            <Statistic
              title="总问卷数"
              value={stats?.total_surveys || 0}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
          {(stats?.status_stats || []).map((stat: { status: string; count: number }) => {
            const statusNameMap = {
              'draft': '草稿',
              'published': '已发布',
              'ended': '已结束'
            };
            const statusName = statusNameMap[stat.status as keyof typeof statusNameMap] || stat.status;
            return (
              <Card key={stat.status}>
                <Statistic
                  title={`${statusName}问卷`}
                  value={stat.count}
                  valueStyle={{ color: stat.status === 'published' ? '#3f8600' : '#cf1322' }}
                />
              </Card>
            );
          })}
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          添加问卷
        </Button>
      </div>

      <Table 
        columns={columns} 
        dataSource={surveys} 
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        style={{ marginBottom: 16 }}
      />

      <Modal
        title={editingSurvey ? '编辑问卷' : '添加问卷'}
        open={isEditorVisible}
        onCancel={() => {
          setIsEditorVisible(false);
        }}
        width={800}
        footer={null}
      >
        <SurveyEditor
          initialData={editingSurvey || undefined}
          onSave={handleSave}
          onCancel={() => setIsEditorVisible(false)}
        />
      </Modal>

      <Modal
        title="问卷预览"
        open={isPreviewVisible}
        onCancel={() => setIsPreviewVisible(false)}
        width={800}
        footer={null}
      >
        {previewSurvey && <SurveyPreview survey={previewSurvey} />}
      </Modal>
    </div>
  );
};

export default QuestionnaireManagement; 